import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import { TALENT_API_BASE, loadTalentCredits } from '../TalentSearch/talentAccount';
import React, { useEffect, useState, useRef } from 'react';
import jsPDF from "jspdf";
import { useNavigate, useLocation } from 'react-router-dom';
import { database, auth } from '../../firebase';
import axios from 'axios';
import './TalentProfile.css';
import TalentContactLookup from './TalentContactLookup';
import TalentQualifications from './TalentQualifications';
import ProfilePhoto, { getProfilePhoto } from '../ProfilePhoto';
import TalentPosition from '../TalentSearch/TalentPosition';
import TalentMatches from '../TalentSearch/TalentMatches';
// Using FontAwesome kit via <i> tags (consistent with rest of app)
import { ref, get, runTransaction, onValue } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAccessControl } from '../../hooks/useAccessControl';
import useCompanyUID from '../../hooks/useCompanyUID';

function safeFirebaseKey(key) {
  return key.replace(/@/g, '_at_').replace(/\./g, '_dot_');
}

const TalentProfile = () => {
  useUiLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile: initialProfile, searchState, startUnlock } = location.state || {};
  const [profileData, setProfileData] = useState(initialProfile);
  const profile = profileData || initialProfile;

  // i18n hooks
  const { t, i18n } = useTranslation('sourcing');
  const { isRTL, language } = useLanguage();
  const { companyUID, loading: companyUidLoading } = useCompanyUID();
  const { subscription, planId } = useAccessControl();

  const getAnalysisLanguageCode = () => {
    const rawLanguage = language || i18n?.language || 'en';
    return String(rawLanguage).toLowerCase().startsWith('ar') ? 'ar' : 'en';
  };

  const getAnalysisStorageKey = (profileId) => `${profileId}__${getAnalysisLanguageCode()}`;

  // Set page title
  useEffect(() => {
    if (profile?.name) {
      document.title = `${profile.name} - JOBSEEQR | Talent Search Engine`;
    } else {
      document.title = "Talent Profile - JOBSEEQR | Talent Search Engine";
    }
  }, [profile]);

  // Ensure FontAwesome kit is loaded (handles direct loads to this route)
  useEffect(() => {
    const existing = document.querySelector('script[src="https://kit.fontawesome.com/f6e3baed53.js"]');
    if (!existing) {
      const fontAwesome = document.createElement('script');
      fontAwesome.src = 'https://kit.fontawesome.com/f6e3baed53.js';
      fontAwesome.crossOrigin = 'anonymous';
      document.head.appendChild(fontAwesome);
    }
  }, []);

  // Contact / Resume / LinkedIn
  const [email, setEmail] = useState('');
  const [phoneNum, setPhoneNum] = useState('');
  const [personLocation, setPersonLocation] = useState('');
  const [resumeLink, setResumeLink] = useState(null);
  const [profileLink, setProfileLink] = useState('');

  // CV Data for internal profiles
  const [cvData, setCvData] = useState(null);

  // Copy states
  const [isEmailCopied, setIsEmailCopied] = useState(false);
  const [isPhoneNumberCopied, setIsPhoneNumberCopied] = useState(false);

  // Shortlist states
  const [isShortlistPopupOpen, setIsShortlistPopupOpen] = useState(false);
  const [shortlists, setShortlists] = useState([]);
  const [isAddShortlistOpen, setIsAddShortlistOpen] = useState(false);
  const [newShortlistName, setNewShortlistName] = useState('');
  const [userUid, setUserUid] = useState(null);
  const accountUid = companyUID || (!companyUidLoading ? userUid : '');

  // Check if this is an external talent profile
  const isExternalProfile = ['signalhire', 'crustdata'].includes(profile?.metadata?.source) ||
    profile?.metadata?.pinecone_id?.startsWith('sh_') || profile?.metadata?.pinecone_id?.startsWith('cd_');

  // **NEW**: Loading state for the profile data
  const [isFetchingProfileDetails, setIsFetchingProfileDetails] = useState(true);
  const [isUnlockingProfile, setIsUnlockingProfile] = useState(false);
  const isLoadingProfile = isFetchingProfileDetails || (isExternalProfile && isUnlockingProfile);
  const [creditRefundChecked, setCreditRefundChecked] = useState(false);

  // Analysis State
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisCredits, setAnalysisCredits] = useState(20);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [hasUnlimitedPlan, setHasUnlimitedPlan] = useState(false);
  const hasUnlimitedSubscription = Boolean(
    subscription?.status === 'unlimited' ||
    subscription?.unlimited === true ||
    subscription?.credits === 'unlimited' ||
    subscription?.tseCredits === 'unlimited' ||
    subscription?.features?.creditsPerMonth === 'unlimited' ||
    planId === 'tse_custom' ||
    planId === 'bundle_custom'
  );

  // UI State for new sections
  const [showAllWork, setShowAllWork] = useState(false);
  const [showAllEdu, setShowAllEdu] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);


  const [isAIExpanded, setIsAIExpanded] = useState(true);
  const [isCareerExpanded, setIsCareerExpanded] = useState(true);

  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const toggleDescription = (key) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const initialLoadRef = useRef(true);
  const didRequestUnlockRef = useRef(false);

  const extractProfileFromUnlock = (payload) => {
    if (!payload) return null;
    if (payload.metadata) return payload;
    if (payload.normalized?.metadata) return payload.normalized;
    return null;
  };

  const mergeProfileData = (baseProfile, incomingProfile) => {
    if (!baseProfile) return incomingProfile;
    if (!incomingProfile) return baseProfile;
    return {
      ...baseProfile,
      ...incomingProfile,
      nationality_tag: incomingProfile.nationality_tag || baseProfile.nationality_tag,
      metadata: {
        ...baseProfile.metadata,
        ...incomingProfile.metadata,
        // Enriched data is shared across searches; keep this search's wording.
        query_matches: baseProfile.metadata?.query_matches || incomingProfile.metadata?.query_matches || [],
        nationality_tag: incomingProfile.metadata?.nationality_tag || baseProfile.metadata?.nationality_tag,
        full_cv: incomingProfile.metadata?.full_cv || baseProfile.metadata?.full_cv
      }
    };
  };

  useEffect(() => {
    if (!initialProfile) return;
    setProfileData(initialProfile);
    setIsFetchingProfileDetails(true);
    setIsUnlockingProfile(false);
    didRequestUnlockRef.current = false;
    initialLoadRef.current = true;
  }, [initialProfile]);

  // Helper to get work experience from either source
  const getWorkExperience = () => {
    // First check full_cv (external profiles)
    if (profile.metadata.full_cv?.work_experience) {
      return Array.isArray(profile.metadata.full_cv.work_experience)
        ? profile.metadata.full_cv.work_experience
        : [];
    }
    // For internal profiles, check cvData
    if (cvData?.work_experience) {
      return Array.isArray(cvData.work_experience) ? cvData.work_experience : [];
    }
    // Fallback for JOBSEEQR profiles OR SignalHire profiles with string experience
    if (profile.metadata.experience) {
      if (Array.isArray(profile.metadata.experience)) {
        // If array of strings, convert to objects
        return profile.metadata.experience.map(expStr => {
          // Attempt to parse "Role at Company" or "Role, Company"
          let title = expStr;
          let company = '';
          if (expStr.includes(' at ')) {
            [title, company] = expStr.split(' at ');
          } else if (expStr.includes(',')) {
            [title, company] = expStr.split(',');
          }
          return {
            title: title.trim(),
            company: company ? company.trim() : '',
            date_range: '',
            description: ''
          };
        });
      }
      // Single string (newline separated?)
      const lines = profile.metadata.experience.split('\n');
      return lines.map(line => {
        // Parse logic
        let title = line;
        let company = '';
        if (line.includes(' at ')) {
          [title, company] = line.split(' at ');
        } else if (line.includes(',')) {
          [title, company] = line.split(',');
        }
        return {
          title: title.trim(),
          company: company ? company.trim() : '',
          date_range: '',
          description: ''
        };
      });
    }
    return [];
  };

  // Helper to get education from either source
  const getEducation = () => {
    // First check full_cv (external profiles)
    if (profile.metadata.full_cv?.education) {
      return Array.isArray(profile.metadata.full_cv.education)
        ? profile.metadata.full_cv.education
        : [];
    }
    // For internal profiles, check cvData
    if (cvData?.education) {
      return Array.isArray(cvData.education) ? cvData.education : [];
    }
    // Fallback for SignalHire or JOBSEEQR profiles with string education
    if (profile.metadata.education) {
      if (Array.isArray(profile.metadata.education)) {
        return profile.metadata.education.map(eduStr => {
          // Parse "Degree at School" or "Degree, School"
          let degree = eduStr;
          let school = '';
          if (eduStr.includes(' at ')) {
            [degree, school] = eduStr.split(' at ');
          } else if (eduStr.includes(',')) {
            [degree, school] = eduStr.split(',');
          }
          return {
            degree: degree.trim(),
            field_of_study: '',
            school: school ? school.trim() : '',
            date_range: '',
            description: ''
          };
        });
      }
      // Single string (newline separated)
      const lines = profile.metadata.education.split('\n');
      return lines.map(line => {
        let degree = line;
        let school = '';
        if (line.includes(' at ')) {
          [degree, school] = line.split(' at ');
        } else if (line.includes(',')) {
          [degree, school] = line.split(',');
        }
        return {
          degree: degree.trim(),
          field_of_study: '',
          school: school ? school.trim() : '',
          date_range: '',
          description: ''
        };
      });
    }
    return [];
  };

  // Helper to get certifications from either source
  const getCertifications = () => {
    // First check full_cv (external profiles)
    if (profile.metadata.full_cv?.certifications) {
      return Array.isArray(profile.metadata.full_cv.certifications)
        ? profile.metadata.full_cv.certifications
        : [];
    }
    // For internal profiles, check cvData
    if (cvData?.certifications) {
      return Array.isArray(cvData.certifications) ? cvData.certifications : [];
    }
    // Fallback for SignalHire profiles with certifications string/array
    if (profile.metadata.certifications) {
      if (Array.isArray(profile.metadata.certifications)) {
        return profile.metadata.certifications.map(cert =>
          typeof cert === 'string' ? { name: cert, issuer: '', date: '' } : cert
        );
      }
      // Single string (newline separated)
      const lines = profile.metadata.certifications.split('\n');
      return lines.map(line => ({ name: line.trim(), issuer: '', date: '' }));
    }
    return [];
  };

  // Format individual date string into localized Month Year
  const formatSingleDate = (str, isArabic) => {
    if (!str) return '';
    const s = str.trim();
    if (/^(present|current|حالي|الحاضر|حتى الآن)$/i.test(s)) {
      return isArabic ? t('talentProfile.present', 'الحاضر') : t('talentProfile.present', 'Present');
    }
    // Check if 4-digit year only
    if (/^\d{4}$/.test(s)) {
      return s;
    }
    // Match ISO timestamp or date: YYYY-MM or YYYY-MM-DD
    const isoMatch = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const monthIndex = parseInt(isoMatch[2], 10) - 1;
      const d = new Date(Date.UTC(year, monthIndex, 1));
      return new Intl.DateTimeFormat(isArabic ? 'ar-u-nu-latn' : 'en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(d);
    }
    // Match text format e.g. "Jul 2020" or "July 2020"
    const textMatch = s.match(/^([a-zA-Z]+)\s+(\d{4})$/);
    if (textMatch) {
      const monthIndex = new Date(Date.parse(`${textMatch[1]} 1, 2000`)).getMonth();
      if (!isNaN(monthIndex)) {
        const d = new Date(Date.UTC(parseInt(textMatch[2], 10), monthIndex, 1));
        return new Intl.DateTimeFormat(isArabic ? 'ar-u-nu-latn' : 'en-US', {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(d);
      }
    }
    // Fallback Date parser
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const year = s.includes('T') ? d.getUTCFullYear() : d.getFullYear();
      const month = s.includes('T') ? d.getUTCMonth() : d.getMonth();
      const utcDate = new Date(Date.UTC(year, month, 1));
      return new Intl.DateTimeFormat(isArabic ? 'ar-u-nu-latn' : 'en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(utcDate);
    }
    return s;
  };

  // Localized date range formatter (e.g. "Sep 2024 – Dec 2024" or "سبتمبر 2024 – ديسمبر 2024")
  const formatDateRange = (dateRange) => {
    if (!dateRange || typeof dateRange !== 'string') return '';
    const trimmed = dateRange.trim();
    if (!trimmed) return '';

    const isArabic = Boolean((language || i18n?.language || '').startsWith('ar') || isRTL);
    const sep = trimmed.includes('→') ? '→' : (trimmed.includes(' - ') ? ' - ' : (trimmed.includes('–') ? '–' : null));
    const parts = sep ? trimmed.split(sep) : [trimmed];

    const formattedParts = parts.map((part) => formatSingleDate(part, isArabic));
    return formattedParts.join(' – ');
  };

  // Parse experience dates safely (supports ISO, text, Arabic months, YYYY, etc.)
  const parseExperienceDate = (str, isEnd = false) => {
    if (!str || typeof str !== 'string') return null;
    const s = str.trim();
    if (!s) return null;

    if (/^(present|current|حالي|الحاضر|حتى الآن|today|now)$/i.test(s)) {
      return new Date();
    }

    // Match 4-digit year only: "2024"
    if (/^\d{4}$/.test(s)) {
      const year = parseInt(s, 10);
      if (year < 1950 || year > 2100) return null;
      return isEnd ? new Date(Date.UTC(year, 11, 31, 23, 59, 59)) : new Date(Date.UTC(year, 0, 1));
    }

    // Match ISO timestamp or date: "2024-09" or "2024-09-01" or "2024-09-01T..."
    const isoMatch = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10) - 1;
      const day = isoMatch[3] ? parseInt(isoMatch[3], 10) : (isEnd ? 28 : 1);
      if (year < 1950 || year > 2100 || month < 0 || month > 11) return null;
      return new Date(Date.UTC(year, month, day));
    }

    // Match MM/YYYY or MM-YYYY
    const slashMatch = s.match(/^(\d{1,2})[/-](\d{4})$/);
    if (slashMatch) {
      const month = parseInt(slashMatch[1], 10) - 1;
      const year = parseInt(slashMatch[2], 10);
      if (year >= 1950 && year <= 2100 && month >= 0 && month <= 11) {
        return new Date(Date.UTC(year, month, isEnd ? 28 : 1));
      }
    }

    // Match "Sep 2024" or "September 2024"
    const textMatch = s.match(/^([a-zA-Z]+)\s+(\d{4})$/);
    if (textMatch) {
      const parsedMonth = new Date(Date.parse(`${textMatch[1]} 1, 2000`)).getMonth();
      const year = parseInt(textMatch[2], 10);
      if (!isNaN(parsedMonth) && year >= 1950 && year <= 2100) {
        return new Date(Date.UTC(year, parsedMonth, isEnd ? 28 : 1));
      }
    }

    // Arabic month names e.g. "سبتمبر 2024"
    const AR_MONTHS = {
      يناير: 0, 'كانون الثاني': 0,
      فبراير: 1, شباط: 1,
      مارس: 2, آذار: 2,
      أبريل: 3, نيسان: 3,
      مايو: 4, أيار: 4,
      يونيو: 5, حزيران: 5,
      يوليو: 6, تموز: 6,
      أغسطس: 7, آب: 7,
      سبتمبر: 8, أيلول: 8,
      أكتوبر: 9, 'تشرين الأول': 9,
      نوفمبر: 10, 'تشرين الثاني': 10,
      ديسمبر: 11, 'كانون الأول': 11,
    };
    for (const [arMonth, monthIdx] of Object.entries(AR_MONTHS)) {
      if (s.includes(arMonth)) {
        const yearMatch = s.match(/\d{4}/);
        if (yearMatch) {
          const year = parseInt(yearMatch[0], 10);
          if (year >= 1950 && year <= 2100) {
            return new Date(Date.UTC(year, monthIdx, isEnd ? 28 : 1));
          }
        }
      }
    }

    // Fallback Date parser
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const year = s.includes('T') ? d.getUTCFullYear() : d.getFullYear();
      if (year >= 1950 && year <= 2100) {
        return d;
      }
    }

    return null;
  };

  // Calculate years of experience (merges overlapping intervals; respects true calendar career duration)
  const calculateYearsOfExperience = () => {
    const jobs = getWorkExperience();
    if (!jobs || jobs.length === 0) return null;

    const now = new Date();
    const nowTime = now.getTime();
    const intervals = [];
    let earliestStartDate = null;

    jobs.forEach(job => {
      const rawRange = job.date_range || job.dateRange || job.dates ||
        (job.start_date ? `${job.start_date} - ${job.end_date || 'Present'}` : '') ||
        (job.startDate ? `${job.startDate} - ${job.endDate || 'Present'}` : '');
      if (!rawRange || typeof rawRange !== 'string') return;

      let startStr = '';
      let endStr = '';
      const sepMatch = rawRange.match(/\s*(?:→|–|—| - |-| to | إلى )\s*/i);
      if (sepMatch) {
        const idx = sepMatch.index;
        startStr = rawRange.slice(0, idx).trim();
        endStr = rawRange.slice(idx + sepMatch[0].length).trim();
      } else {
        startStr = rawRange.trim();
        endStr = '';
      }

      const startDate = parseExperienceDate(startStr, false);
      if (!startDate) return;

      let endDate = endStr ? parseExperienceDate(endStr, true) : (startDate ? new Date(Date.UTC(startDate.getUTCFullYear(), 11, 31)) : null);
      if (!endDate) endDate = now;

      // Start date cannot be in the future
      if (startDate.getTime() > nowTime) return;
      // End date clamped to current time (experience is only accrued up to now)
      const clampedEnd = new Date(Math.min(endDate.getTime(), nowTime));
      if (clampedEnd.getTime() < startDate.getTime()) return;

      if (!earliestStartDate || startDate.getTime() < earliestStartDate.getTime()) {
        earliestStartDate = startDate;
      }

      intervals.push([startDate.getTime(), clampedEnd.getTime()]);
    });

    if (intervals.length === 0 || !earliestStartDate) return null;

    // Sort intervals by start timestamp ascending
    intervals.sort((a, b) => a[0] - b[0]);

    // Merge overlapping intervals
    const merged = [];
    for (const [start, end] of intervals) {
      if (merged.length > 0 && start <= merged[merged.length - 1][1]) {
        merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
      } else {
        merged.push([start, end]);
      }
    }

    // Sum non-overlapping duration in days
    const msPerDay = 1000 * 60 * 60 * 24;
    let totalDays = 0;
    for (const [start, end] of merged) {
      totalDays += (end - start) / msPerDay;
    }

    // Hard ceiling: total experience can never exceed calendar time since earliest start date
    const maxCareerDays = (nowTime - earliestStartDate.getTime()) / msPerDay;
    totalDays = Math.min(totalDays, maxCareerDays);

    const totalYears = totalDays / 365.25;
    if (totalYears < 0.5) return null;

    const years = Math.max(1, Math.floor(totalYears));
    const earliestYear = earliestStartDate.getUTCFullYear();
    const currentYear = now.getUTCFullYear();
    const maxPossibleYears = Math.max(1, currentYear - earliestYear + 1);

    return Math.min(years, maxPossibleYears);
  };

  // Tooltip positioning state
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipIconRef = useRef(null);

  const API_BASE_URL = TALENT_API_BASE;

  useEffect(() => {
    if (!profile) {
      // If there's no profile data passed, go back
      navigate(-1);
      return;
    }

    if (initialLoadRef.current) {
      setIsFetchingProfileDetails(true);
    }

    const fetchAdditionalData = async () => {
      try {
        const userUid = profile.metadata.user_uid;

        if (!userUid) {
          setEmail(profile.metadata.email || '');
          setPhoneNum(profile.metadata.phoneNumber || '');
          setProfileLink(profile.metadata.linkedin_url || '');
          setResumeLink(profile.metadata.resumeLink || null);

          if (profile.metadata.location) {
            setPersonLocation(profile.metadata.location);
          } else if (profile.metadata.full_cv) {
            setPersonLocation(profile.metadata.full_cv.contact.address || 'Location Not Provided');
          } else {
            setPersonLocation('Location Not Provided');
          }

          return;
        }

        // 1) Email
        if (profile.metadata.email && profile.metadata.email !== 'nan') {
          setEmail(profile.metadata.email);
        } else {
          try {
            const response = await axios.post(
              'https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail',
              { uid: userUid }
            );
            setEmail(response.data.email || 'No Email On File');
          } catch (error) {
            console.log('Error fetching user email:', error);
            setEmail('No Email On File');
          }
        }

        // 2) Resume Link
        if (profile.metadata.resumeLink) {
          setResumeLink(profile.metadata.resumeLink);
        } else {
          const resumeRef = ref(database, `people/${userUid}/cv/Link`);
          try {
            const resumeSnapshot = await get(resumeRef);
            setResumeLink(resumeSnapshot.exists() ? resumeSnapshot.val() : '');
          } catch (error) {
            console.error('Error fetching resume link:', error);
            setResumeLink('');
          }
        }

        // 3) Phone Number
        if (profile.metadata.phoneNumber) {
          setPhoneNum(profile.metadata.phoneNumber);
        } else {
          const phoneNumberRef = ref(database, `people/${userUid}/phoneNumber`);
          try {
            const phoneNumberSnapshot = await get(phoneNumberRef);
            setPhoneNum(phoneNumberSnapshot.exists() ? phoneNumberSnapshot.val() : 'No Phone Number');
          } catch (error) {
            console.error('Error fetching phone num:', error);
            setPhoneNum('No Phone Number');
          }
        }

        // 4) LinkedIn
        if (profile.metadata.linkedin_url) {
          setProfileLink(profile.metadata.linkedin_url);
        } else {
          const profileLinkRef = ref(database, `people/${userUid}/linkedin`);
          try {
            const profileLinkSnapshot = await get(profileLinkRef);
            setProfileLink(profileLinkSnapshot.exists() ? profileLinkSnapshot.val() : '');
          } catch (error) {
            console.error('Error fetching profile link:', error);
            setProfileLink('');
          }
        }

        // 5) Location
        // First check if location is already in metadata (from shortlist)
        if (profile.metadata.location) {
          setPersonLocation(profile.metadata.location);
        } else if (profile.metadata.full_cv) {
          setPersonLocation(profile.metadata.full_cv.contact.address || 'Location Not Provided');
        } else {
          const locationRef = ref(database, `people/${userUid}/location`);
          try {
            const locationSnapshot = await get(locationRef);
            setPersonLocation(locationSnapshot.exists() ? locationSnapshot.val() : 'Location Not Provided');
          } catch (error) {
            console.error('Error fetching location:', error);
            setPersonLocation('Location Not Provided');
          }
        }

        // 6) Fetch CV data for internal profiles (JOBSEEQR signups)
        // We ALWAYS try to fetch this for internal profiles because some profiles
        // may have a stub `full_cv` object with no work_experience / education.
        // In those cases, we still want to fall back to the richer data stored
        // under people/{userUid}/cv in Firebase.
        if (profile.metadata.user_uid) {
          try {
            // Try multiple possible paths for CV data
            const cvRef = ref(database, `people/${userUid}/cv`);
            const cvSnapshot = await get(cvRef);
            if (cvSnapshot.exists()) {
              const cv = cvSnapshot.val();
              // Check if work_experience and education are directly in cv
              if (cv.work_experience || cv.education || cv.certifications) {
                setCvData({
                  work_experience: cv.work_experience || [],
                  education: cv.education || [],
                  certifications: cv.certifications || []
                });
              } else {
                // Try parsing from parsedData if available
                const parsedData = cv.parsedData || {};
                if (parsedData.work_experience || parsedData.education || parsedData.certifications) {
                  setCvData({
                    work_experience: parsedData.work_experience || [],
                    education: parsedData.education || [],
                    certifications: parsedData.certifications || []
                  });
                }
              }
            }
          } catch (error) {
            console.error('Error fetching CV data:', error);
          }
        }

      } catch (error) {
        console.error('Error fetching additional data:', error);
      }
    };

    // Call the function, then set loading to false in .finally()
    fetchAdditionalData().finally(() => {
      if (initialLoadRef.current) {
        setIsFetchingProfileDetails(false);
        initialLoadRef.current = false;
      }
    });
  }, [profile, navigate]);

  useEffect(() => {
    if (!profile || !isExternalProfile) return;
    const pineconeId = profile.metadata?.pinecone_id;
    if (!pineconeId) return;

    const fullCv = profile.metadata?.full_cv;
    const hasWork = Array.isArray(fullCv?.work_experience) && fullCv.work_experience.length > 0;
    const hasEducation = Array.isArray(fullCv?.education) && fullCv.education.length > 0;
    // Crustdata search includes work/education but omits skills/certificates.
    // Consult the backend on each open; it owns the persistent 90-day cache.
    const shouldFetchFullProfile = pineconeId.startsWith('cd_') || startUnlock || !hasWork || !hasEducation;

    if (!shouldFetchFullProfile || didRequestUnlockRef.current) return;
    didRequestUnlockRef.current = true;

    setIsUnlockingProfile(true);
    const requestConfig = accountUid ? { params: { user_uid: accountUid } } : {};

    axios.post(
      `${API_BASE_URL}/v2/unlock_profile`,
      { profile_id: pineconeId },
      requestConfig
    ).then((response) => {
      const incomingProfile = extractProfileFromUnlock(response.data?.profile) ||
        extractProfileFromUnlock(response.data?.normalized) ||
        extractProfileFromUnlock(response.data);

      if (incomingProfile) {
        setProfileData((current) => mergeProfileData(current, incomingProfile));
      }
    }).catch((error) => {
      console.error('Error fetching external talent profile:', error);
    }).finally(() => {
      setIsUnlockingProfile(false);
    });
  }, [accountUid, profile, isExternalProfile, startUnlock]);

  // Check if contact info is missing and refund credit if needed
  useEffect(() => {
    const checkAndRefundCredit = async () => {
      // Only run once per profile load
      if (creditRefundChecked || isLoadingProfile || isExternalProfile) return;

      // Only refund if this was a profile that was unlocked (has pinecone_id)
      if (!profile?.metadata?.pinecone_id) return;

      // Only refund if this profile doesn't come from a shortlist
      if (!accountUid) return;

      const hasNoEmail = !email || email === 'No Email On File' || email === 'nan' || email === '';
      const hasNoPhone = !phoneNum || phoneNum === 'No Phone Number' || phoneNum === '';

      if (hasNoEmail && hasNoPhone) {
        console.log('[Credit Refund] No contact info found - refunding credit');
        try {
          await axios.post(
            `${API_BASE_URL}/refund_credit`,
            { profileId: profile.metadata.pinecone_id },
            { params: { user_uid: accountUid } }
          );
          console.log('[Credit Refund] Credit refunded successfully');
        } catch (error) {
          console.error('[Credit Refund] Error refunding credit:', error);
        }
      } else {
        console.log('[Credit Refund] Contact info found - no refund needed', {
          hasEmail: !hasNoEmail,
          hasPhone: !hasNoPhone
        });
      }

      setCreditRefundChecked(true);
    };

    checkAndRefundCredit();
  }, [email, phoneNum, isLoadingProfile, creditRefundChecked, profile, accountUid, isExternalProfile]);

  // Watch for auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUid(user.uid);
      } else {
        setUserUid(null);
        navigate('/login');
      }
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
    // eslint-disable-next-line
  }, []);

  const fetchAnalysisData = async (uid) => {
    if (!uid || !profile?.metadata?.pinecone_id) return;

    // 1. Check Credits
    try {
      const credits = await loadTalentCredits(uid, hasUnlimitedSubscription);
      setHasUnlimitedPlan(credits.unlimited);
      setAnalysisCredits(credits.analysis);

    } catch (err) {
      console.error("Error fetching credits:", err);
    }

    // 2. Check Existing Analysis
    try {
      const safeId = safeFirebaseKey(getAnalysisStorageKey(profile.metadata.pinecone_id));
      const analysisRef = ref(database, `companies/${uid}/profile_analyses/${safeId}`);
      const analysisSnap = await get(analysisRef);
      if (analysisSnap.exists()) {
        setAnalysisData(analysisSnap.val());
      } else {
        setAnalysisData(null);
      }
    } catch (err) {
      console.error("Error fetching analysis:", err);
    }
  };

  useEffect(() => {
    if (!accountUid || !profile?.metadata?.pinecone_id) return;
    fetchAnalysisData(accountUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, profile?.metadata?.pinecone_id, language, i18n?.language, hasUnlimitedSubscription]);

  const fetchShortlists = (uid) => {
    const userId = uid || accountUid;
    if (!userId) return;
    const shortlistsRef = ref(database, `companies/${userId}/tal_shortlists`);
    onValue(shortlistsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const fetchedShortlists = Object.keys(data).map((shortlistName) => {
          const profilesObj = data[shortlistName].profiles || {};
          const profilesCount = Object.keys(profilesObj).length;
          return {
            name: shortlistName,
            profilesCount,
          };
        });
        setShortlists(fetchedShortlists);
      } else {
        setShortlists([]);
      }
    });
  };

  useEffect(() => {
    if (!accountUid) return;
    fetchShortlists(accountUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid]);

  // Copy email
  const handleCopyEmail = () => {
    if (email && email !== 'No Email On File') {
      navigator.clipboard
        .writeText(email)
        .then(() => {
          setIsEmailCopied(true);
          setTimeout(() => {
            setIsEmailCopied(false);
          }, 3000);
        })
        .catch((err) => {
          console.error('Could not copy email: ', err);
        });
    }
  };

  // Copy phone
  const handleCopyNumber = () => {
    if (phoneNum && phoneNum !== 'No Phone Number') {
      navigator.clipboard
        .writeText(phoneNum)
        .then(() => {
          setIsPhoneNumberCopied(true);
          setTimeout(() => {
            setIsPhoneNumberCopied(false);
          }, 3000);
        })
        .catch((err) => {
          console.error('Could not copy phone number: ', err);
        });
    }
  };

  const handleOpenResume = () => {
    if (resumeLink) {
      window.open(resumeLink, '_blank');
    } else {
      alert(uiText('No resume available.'));
    }
  };

  const handleBack = () => {
    if (location.state?.fromOutreach && location.state?.campaign) {
      navigate('/outreach', {
        state: {
          view: 'draft',
          campaign: location.state.campaign,
          tab: location.state.tab
        }
      });
    } else {
      navigate(searchState?.sourcingView === 'shortlists' ? `/sourcing?view=shortlists&shortlist=${encodeURIComponent(searchState.selectedShortlist)}` : '/sourcing', { state: { searchState } });
    }
  };

  const handleViewLinkedIn = () => {
    if (profileLink) {
      window.open(profileLink, '_blank');
    } else {
      alert(uiText('No LinkedIn Account'));
    }
  };

  const handleContactUpdate = (data) => {
    if (data.email) setEmail(data.email);
    if (data.phone) setPhoneNum(data.phone);
    if (data.email || data.phone) {
      setProfileData(current => ({
        ...current,
        metadata: {
          ...current.metadata,
          email: data.email || current.metadata.email,
          phoneNumber: data.phone || current.metadata.phoneNumber,
        },
      }));
    }
  };

  // Shortlist logic
  const handleAddToShortlist = () => {
    setIsShortlistPopupOpen(true);
  };

  const handleAddProfileToShortlist = (shortlistName) => {
    const uid = accountUid;
    if (!uid) return;

    const fromPinecone = !!profile.metadata.pinecone_id;
    const key = fromPinecone
      ? profile.metadata.pinecone_id
      : profile.metadata.user_uid || 'unknownUid';

    // Get Clean Role/Company Data
    let cleanCompany = '';
    let cleanTitle = '';

    // 1. Try to get from structured work experience
    const workExp = getWorkExperience();
    if (workExp && workExp.length > 0) {
      // Sort by date if needed, but usually index 0 is latest or current
      // Assuming the list is ordered or 0 is relevant
      cleanCompany = workExp[0].company || '';
      cleanTitle = workExp[0].title || '';
    }

    // 2. Fallback to parsing metadata strings if structured data failed
    if (!cleanCompany && profile.metadata.experience) {
      // Handle "Role, Company, Date" format
      const parts = profile.metadata.experience.split(',');
      if (parts.length >= 2) {
        cleanTitle = parts[0].trim();
        cleanCompany = parts[1].trim();
      } else {
        // Handle "Role at Company" format
        const atParts = profile.metadata.experience.split(' at ');
        if (atParts.length >= 2) {
          cleanTitle = atParts[0].trim();
          cleanCompany = atParts[1].trim();
        }
      }
    }

    // 3. Last resort fallbacks
    if (!cleanCompany) cleanCompany = profile.metadata.company || '';
    if (!cleanTitle) cleanTitle = profile.metadata.jobTitle || profile.metadata.experience || '';

    const profileData = {
      fromPinecone,
      pinecone_id: profile.metadata.pinecone_id || '',
      user_uid: profile.metadata.user_uid || '',
      username: profile.metadata.fullUsername || profile.metadata.username || 'Unknown',
      email: email,
      phoneNumber: phoneNum,
      linkedin: profileLink,
      location: personLocation || 'Location Not Provided',
      photo_url: getProfilePhoto(profile.metadata, profile.metadata.full_cv, cvData, profile),
      query_matches: profile.metadata.query_matches || [],
      tags: profile.metadata.tags || [],
      // Save original raw strings for context
      experience: profile.metadata.experience || '',
      company_info: profile.metadata.company_info || '',
      education: profile.metadata.education || '',
      institution_info: profile.metadata.institution_info || '',
      source: profile.metadata.source || '',
      full_cv: profile.metadata.full_cv || null,
      key_achievements: profile.metadata.key_achievements || '',
      openToWork: profile.metadata.openToWork || false,
      // Save CLEAN separated fields
      company: cleanCompany,
      jobTitle: cleanTitle
    };

    // Create a sanitized version of key without forbidden characters
    const keyForFirebase = key.replace(/[.#$[\]]/g, '_');

    // Use keyForFirebase in your Firebase reference
    const shortlistProfileRef = ref(
      database,
      `companies/${uid}/tal_shortlists/${shortlistName}/profiles/${keyForFirebase}`
    );
    runTransaction(shortlistProfileRef, (current) => ({
      ...profileData,
      ...current,
      full_cv: current?.full_cv || profileData.full_cv,
      photo_url: getProfilePhoto(current, profileData),
      query_matches: profileData.query_matches.length ? profileData.query_matches : current?.query_matches || [],
      email: current?.email || profileData.email,
      phoneNumber: current?.phoneNumber || profileData.phoneNumber,
    }), { applyLocally: false })
      .then(() => {
        setIsShortlistPopupOpen(false);
        alert(uiText('Profile added to shortlist "{{name}}"', { name: shortlistName }));
      })
      .catch((error) => {
        console.error('Error adding profile to shortlist:', error);
      });
  };

  const handleAddShortlistClick = () => {
    setIsAddShortlistOpen(true);
  };

  const handleSaveNewShortlist = () => {
    const uid = accountUid;
    if (!uid || newShortlistName.trim() === '') return;
    const newShortlistRef = ref(database, `companies/${uid}/tal_shortlists/${newShortlistName}`);
    runTransaction(newShortlistRef, (current) => current || { createdAt: Date.now(), profiles: {} },
      { applyLocally: false })
      .then(() => {
        setNewShortlistName('');
        setIsAddShortlistOpen(false);
        fetchShortlists(uid);
      })
      .catch((error) => {
        console.error('Error adding new shortlist:', error);
      });
  };

  const handleRunAnalysis = async () => {
    if (!accountUid) return;
    if (!searchState?.searchText) {
      alert(uiText("No search query found to analyze against."));
      return;
    }

    setIsAnalyzing(true);
    try {
      // Prepare profile data: merge fetched cvData if available (for internal profiles)
      let profileToSend = { ...profile };
      if (cvData && profile.metadata.user_uid) {
        profileToSend = {
          ...profile,
          metadata: {
            ...profile.metadata,
            full_cv: {
              ...profile.metadata.full_cv,
              ...cvData,
              contact: {
                ...(profile.metadata.full_cv?.contact || {}),
                address: personLocation
              }
            }
          }
        };
      }

      const response = await axios.post(`${API_BASE_URL}/analyze_profile`, {
        profile_data: {
          ...profileToSend,
          // Ensure page_content is available for generic text analysis if structure is missing
          text_content: profileToSend.page_content || JSON.stringify(profileToSend.metadata)
        },
        search_query: searchState.searchText,
        response_language: getAnalysisLanguageCode()
      }, {
        params: { user_uid: accountUid }
      });

      const data = response.data;
      setAnalysisData(data);
      // Update local credits
      if (!hasUnlimitedPlan) {
        setAnalysisCredits(prev => Math.max(0, prev - 1));
      }
      setIsAIExpanded(true);
    } catch (error) {
      console.error("Error running analysis:", error);
      alert(error.response?.data?.detail || "Error running analysis");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Download cv
  const handleDownloadCV = () => {
    const cvData = profile.metadata.full_cv;
    if (!cvData) {
      alert(uiText("No CV data available to generate PDF."));
      return;
    }

    // Create a new jsPDF instance in letter format.
    const doc = new jsPDF({
      unit: "pt",
      format: "letter"
    });

    let marginLeft = 40;
    let currentY = 50;
    const lineHeight = 20;
    const pageHeight = doc.internal.pageSize.getHeight();
    const bottomMargin = 40; // Adjust as needed for padding at the bottom

    // Helper: Check if currentY has reached the bottom of the page; if so, add a new page.
    const checkPageOverflow = () => {
      if (currentY > pageHeight - bottomMargin) {
        doc.addPage();
        currentY = 50; // reset to top margin on new page
      }
    };

    // Helper: Add a section header and check for page overflow.
    const addHeader = (text) => {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(text, marginLeft, currentY);
      currentY += lineHeight + 5;
      checkPageOverflow();
    };

    // Helper: Add a section header and check for page overflow.
    const addSubHeader = (text) => {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const lines = doc.splitTextToSize(text, 500);
      doc.text(lines, marginLeft, currentY);
      currentY += lineHeight + 5;
      checkPageOverflow();
    };

    // Helper: Add a block of text that wraps and check for page overflow.
    const addTextBlock = (text) => {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(text, 500);
      doc.text(lines, marginLeft, currentY);
      currentY += lines.length * lineHeight;
      checkPageOverflow();
    };

    // --- Build the PDF using the full_cv data ---

    // Name (Header)
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(cvData.name, marginLeft, currentY);
    currentY += lineHeight + 10;
    checkPageOverflow();

    // Contact Information
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    if (cvData.contact.email_addresses) {
      const emailText = Array.isArray(cvData.contact.email_addresses)
        ? cvData.contact.email_addresses.join(", ")
        : cvData.contact.email_addresses;
      addTextBlock("Email: " + emailText);
    }
    if (cvData.contact.address) {
      addTextBlock("Address: " + cvData.contact.address);
    }
    if (cvData.contact.linkedin_url) {
      addTextBlock("LinkedIn: " + cvData.contact.linkedin_url);
    }
    currentY += 10;
    checkPageOverflow();

    // About Section
    addHeader("About");
    addTextBlock(cvData.about || "N/A");

    // Work Experience Section
    addHeader("Work Experience");
    if (cvData.work_experience && cvData.work_experience.length > 0) {
      cvData.work_experience.forEach((job, index) => {
        addSubHeader(`${job.title} at ${job.company} (${formatDateRange(job.date_range)})`);
        if (job.description) {
          addTextBlock(job.description);
        }
        currentY += 5; // Extra spacing between entries
        checkPageOverflow();
      });
    } else {
      addTextBlock("No work experience provided.");
    }

    // Education Section
    addHeader("Education");
    if (cvData.education && cvData.education.length > 0) {
      cvData.education.forEach((edu, index) => {
        addSubHeader(`${edu.degree} in ${edu.field_of_study}, ${edu.school} (${formatDateRange(edu.date_range)})`);
        if (edu.description) {
          addTextBlock(edu.description);
        }
        currentY += 5;
        checkPageOverflow();
      });
      currentY += 5;
    } else {
      addTextBlock("No education details provided.");
    }

    const qualifications = getCertifications();
    if (qualifications.length) {
      addHeader('Professional qualifications & certifications');
      qualifications.forEach(cert => {
        addSubHeader(cert.name);
        const details = [cert.issuer, cert.date].filter(Boolean).join(' • ');
        if (details) addTextBlock(details);
        if (cert.expiration_date) addTextBlock('Expires: ' + cert.expiration_date);
        if (cert.source === 'education') addTextBlock('Listed under education; completion is not independently confirmed.');
        if (cert.description) addTextBlock(cert.description);
        if (cert.credential_id) addTextBlock('Credential ID: ' + cert.credential_id);
        if (cert.credential_url) addTextBlock(cert.credential_url);
      });
    }

    // Skills Section
    addHeader("Skills");
    if (cvData.skills && cvData.skills.length > 0) {
      addTextBlock(cvData.skills.join(", "));
      currentY += 5;
    } else {
      addTextBlock("No skills provided.");
    }

    // Languages Section
    addHeader("Languages");
    if (cvData.languages && cvData.languages.length > 0) {
      addTextBlock(cvData.languages.join(", "));
      currentY += 5;
    } else {
      addTextBlock("No languages provided.");
    }

    // Tags Section (optional)
    addHeader("Tags");
    if (cvData.tags && cvData.tags.length > 0) {
      addTextBlock(cvData.tags.join(", "));
      currentY += 5;
    } else {
      addTextBlock("No tags provided.");
    }

    // Generate and download the PDF using the candidate's name.
    const pdfName = cvData.name ? cvData.name.replace(/\s+/g, "_") : "CV";
    doc.save(`${pdfName}-CV.pdf`);
  };



  // ** Check if still loading **
  if (isLoadingProfile) {
    return (
      <div className="loading-section">
        <div className="loading-animation"></div>
        <h2 className="loading-text" style={{ color: "var(--app-text, #ffffff) !important" }}>
          {t('talentProfile.loading', 'Loading Profile Details')}<span className="dots" style={{ color: "var(--app-text, #ffffff) !important" }}>...</span>
        </h2>
      </div>
    );
  }

  // ** Once loaded, show normal UI **
  return (
    <div className="talent-profile-container">
      {/* Header Section */}
      <div className="tp-header-container">
        <div className="tp-header-top">
          <div className="tp-header-left">
            {/* Image Frame */}
            <div className="tp-image-frame-container">
              <div className="tp-image-frame-rotate1"></div>
              <div className="tp-image-frame-rotate2"></div>
              <div className="tp-profile-image-wrapper">
                <ProfilePhoto
                  src={getProfilePhoto(profile.metadata, profile.metadata.full_cv, cvData, profile)}
                  alt={profile.metadata.fullUsername || uiText("Candidate")}
                />
              </div>
            </div>

            {/* Info Column */}
            <div className="tp-info-column">
              <div className="tp-name-row">
                <h1 className="tp-name">
                  {profile.metadata.fullUsername || profile.metadata.username}
                </h1>
                <div className={`profile-source-pill ${profile.metadata.user_uid ? 'source-internal' : 'source-external'}`} style={{ marginInlineStart: 12, verticalAlign: 'middle' }}>
                  {profile.metadata.user_uid ? t('talentProfile.sourceInternal', 'JOBSEEQR') : t('talentProfile.sourceExternal', 'External')}
                </div>

                {/* Info Icon with Tooltip */}
                <div
                  className="profile-source-tooltip-container"
                  onMouseEnter={(e) => {
                    const iconRect = e.currentTarget.querySelector('.profile-source-info-icon').getBoundingClientRect();
                    setTooltipPosition({
                      top: iconRect.top,
                      left: iconRect.left + iconRect.width / 2
                    });
                    setIsTooltipVisible(true);
                  }}
                  onMouseLeave={() => setIsTooltipVisible(false)}
                  style={{ marginInlineStart: 6, verticalAlign: 'middle' }}
                >
                  <i ref={tooltipIconRef} className="fa-solid fa-circle-question profile-source-info-icon"></i>
                  {isTooltipVisible && (
                    <div
                      className="profile-source-tooltip"
                      style={{
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        transform: 'translate(-50%, calc(-100% - 8px))'
                      }}
                    >
                      {profile.metadata.user_uid
                        ? t('talentProfile.tooltipInternal', 'This profile is from a candidate who signed up directly to JOBSEEQR.')
                        : t('talentProfile.tooltipExternal', 'This profile is from one of our Data Partners and did not directly sign up to JOBSEEQR.')}
                    </div>
                  )}
                </div>
              </div>

              <h2 className="tp-role-company">
                <TalentPosition metadata={profile.metadata} language={language} empty={t('talentProfile.roleNotListed', 'Role Not Listed')} />
              </h2>

              {!!profile.metadata.query_matches?.length && <TalentMatches.Badges
                metadata={{ query_matches: profile.metadata.query_matches }} t={t} />}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {(profile.metadata?.nationality_tag || profile.nationality_tag) && (
                  <span
                    title={profile.metadata?.nationality_tag || profile.nationality_tag}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 12px',
                      borderRadius: 14,
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      color: 'var(--app-text, #fff)',
                      background: 'rgba(34,197,94,.80)',
                      border: '1px solid rgba(34,197,94,.30)',
                      fontSize: 13,
                      fontWeight: 600
                    }}
                  >
                    <i className="fa-solid fa-check" aria-hidden="true" style={{ fontSize: 11, flexShrink: 0 }} />
                    <span>{profile.metadata?.nationality_tag || profile.nationality_tag}</span>
                  </span>
                )}
                {profile.metadata.openToWork && (
                  <div className="otw-pill">
                    <span className="otw-dot"></span>
                    {t('results.openToWork', 'Open to Work')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Header Actions */}
          <div className="tp-header-actions">
            <button className="tp-header-btn" onClick={handleAddToShortlist}>
              <i className="fa-solid fa-bookmark"></i>
              <span>{t('talentProfile.saveToShortlist', 'Save to Shortlist')}</span>
            </button>
            <button className="tp-header-btn tp-close-btn" onClick={handleBack}>
              <i className="fa-solid fa-xmark"></i>
              <span>{t('talentProfile.closeProfile', 'Close Profile')}</span>
            </button>
          </div>
        </div>

        {/* Contact Row - full width under header */}
        <div className="tp-contact-row tp-contact-row-full">
          <div className="tp-contact-item" style={{ cursor: 'default' }}>
            <i className="fa-solid fa-location-dot tp-contact-icon"></i>
            <span>{personLocation || t('talentProfile.locationNotProvided', 'Location Not Provided')}</span>
          </div>

          <TalentContactLookup
            external={isExternalProfile}
            profileId={profile?.metadata?.pinecone_id}
            accountUid={accountUid}
            apiBase={API_BASE_URL}
            email={email}
            phone={phoneNum}
            onUpdate={handleContactUpdate}
            onCopyEmail={handleCopyEmail}
            onCopyPhone={handleCopyNumber}
            emailCopied={isEmailCopied}
            phoneCopied={isPhoneNumberCopied}
            t={t}
          />

          {profileLink && (
            <div
              className="tp-contact-item"
              onClick={() => window.open(profileLink, '_blank')}
              style={{ cursor: 'pointer' }}
            >
              <i className="fa-brands fa-linkedin tp-contact-icon"></i>
              <span>{t('talentProfile.viewLinkedIn', 'View LinkedIn')}</span>
            </div>
          )}

          {/* Salary Range - Commented out for now */}
          {/* {profile.metadata.salary_range && (
            <div className="tp-contact-item" style={{ cursor: 'default' }}>
              <i className="fa-solid fa-dollar-sign tp-contact-icon"></i>
              <span>Salary Range - {profile.metadata.salary_range}</span>
              <span className="tp-ai-badge" style={{ marginLeft: 4 }}>AI</span>
            </div>
          )} */}
        </div>

        {/* Bottom Barrier */}
        <div className="tp-header-barrier"></div>
      </div>

      {/* Main Content Layout */}
      <div className="tp-content-container">
        {/* Left Column: Video (if available) */}
        {profile.metadata.video_url && (
          <div className="tp-video-section">
            <div className="tp-video-container">
              <video
                src={profile.metadata.video_url}
                className="tp-video-player"
                controls
                poster={profile.metadata.video_thumbnail || ""}
              />
            </div>
          </div>
        )}

        {/* Right Column: Info Sections */}
        <div className="tp-info-sections">
          {/* AI Section */}
          <div className="tp-section-container">
            <div className="tp-section-header">
              <div className="tp-section-title-group">
                <div className="tp-section-icon">
                  <i className="fa-solid fa-gem" style={{ fontSize: 20 }}></i>
                </div>
                <h3 className="tp-section-title">{t('talentProfile.talentIntelligence', 'Talent Intelligence Profile')}<sup className="tp-ai-badge">{uiText("AI")}</sup></h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="tp-header-btn" onClick={handleRunAnalysis} disabled={isAnalyzing || (!hasUnlimitedPlan && analysisCredits <= 0)}>
                  {isAnalyzing ? (
                    <>
                      <span style={{ color: 'rgba(255,255,255,0.7)', marginInlineEnd: 10 }}>{t('talentProfile.analyzing', 'Analyzing, please wait...')}</span>
                      <div className="tp-analysis-loading-spinner"></div>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                      <span>{analysisData ? t('talentProfile.reRunAnalysis', 'Re-run Analysis') : t('talentProfile.runAnalysis', 'Run Analysis')} ({hasUnlimitedPlan ? '∞' : `${analysisCredits}/20`})</span>
                    </>
                  )}
                </button>
                <button className="tp-collapse-btn" onClick={() => setIsAIExpanded(!isAIExpanded)}>
                  <i className={`fa-solid ${isAIExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                </button>
              </div>
            </div>

            {isAIExpanded && (
              <div className="tp-ai-grid">
                {analysisData ? (
                  <>
                    {/* Profile Summary with Fitness Score */}
                    <div>
                      <h4 className="tp-ai-sub-header">{t('talentProfile.profileSummary', 'Profile Summary')}</h4>
                      <div className="tp-profile-summary-row">
                        {/* Fitness Score Value */}
                        <div className="tp-fitness-score">
                          {(() => {
                            const score = analysisData.fitness_rating;
                            let color = '#40EB6E'; // Green for great fit
                            let label = t('talentProfile.greatFit', 'Great Fit');
                            if (score < 50) { color = '#EB4040'; label = t('talentProfile.notGoodFit', 'Not a Good Fit'); }
                            else if (score < 75) { color = '#EBDD40'; label = t('talentProfile.averageFit', 'Average Fit'); }

                            return (
                              <>
                                <span className="tp-fitness-value" style={{ color }}>{score}%</span>
                                <span className="tp-fitness-label" style={{ color }}>{uiText(label)}</span>
                              </>
                            );
                          })()}
                        </div>

                        {/* Summary Text */}
                        <p className="tp-ai-text" style={{ flex: 1 }}>
                          {analysisData.summary}
                        </p>
                      </div>
                    </div>

                    {/* Strength Signals */}
                    <div>
                      <h4 className="tp-ai-sub-header">{t('talentProfile.strengthSignals', 'Strength Signals')}</h4>
                      <div className="tp-strength-signals">
                        {/* Bullet Points */}
                        <div className="tp-strength-signals-list">
                          {analysisData.strength_signals.map((signal, idx) => (
                            <div className="tp-strength-bullet" key={idx}>{signal}</div>
                          ))}
                        </div>

                        {/* Score Circles */}
                        <div className="tp-strength-scores">
                          {analysisData.top_attributes.map((attr, idx) => (
                            <div className="tp-signal-item" key={idx}>
                              <div
                                className="tp-signal-circle"
                                style={{ '--score-percent': `${(attr.score / 10) * 100}%` }}
                              >
                                <span className="tp-signal-circle-inner">{attr.score}</span>
                              </div>
                              <span className="tp-signal-label">
                                {attr.name.split(' ').map((word, i) => <React.Fragment key={i}>{word}{i < attr.name.split(' ').length - 1 && <br />}</React.Fragment>)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Due Diligence & Recommendations */}
                    <div>
                      <h4 className="tp-ai-sub-header">{t('talentProfile.dueDiligence', 'Due Diligence & Recommendations')}</h4>
                      <div className="tp-dd-notes">
                        {analysisData.due_diligence_tags.length > 0 ? (
                          analysisData.due_diligence_tags.map((tag, idx) => (
                            <span className="tp-dd-pill" key={idx}>
                              <span className="tp-dd-dot"></span>
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)', fontStyle: 'italic' }}>{t('talentProfile.noFlagsDetected', 'No flags detected.')}</span>
                        )}
                      </div>

                      {/* Recommendation with Info Icon */}
                      {analysisData.due_diligence_notes && (
                        <div className="tp-dd-recommendation">
                          <div className="tp-dd-info-icon">
                            <i className="fa-solid fa-info"></i>
                          </div>
                          <p className="tp-dd-desc">
                            {analysisData.due_diligence_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p className="tp-description" style={{ fontStyle: 'italic', marginBottom: 20 }}>
                      {t('talentProfile.runAnalysisToUnlock', 'Run analysis to unlock AI insights for this candidate.')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Career So Far Section */}
          <div className="tp-section-container">
            <div className="tp-section-header">
              <div className="tp-section-title-group">
                <div className="tp-section-icon">
                  <i className="fa-solid fa-briefcase" style={{ fontSize: 20 }}></i>
                </div>
                <h3 className="tp-section-title">{t('talentProfile.careerSoFar', "{{name}}'s Career So Far", { name: profile.metadata.username ? profile.metadata.username.split(' ')[0] : 'Candidate' })}</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {(resumeLink || profile.metadata.full_cv) && (
                  <button className="tp-header-btn" onClick={resumeLink ? handleOpenResume : (profile.metadata.full_cv ? handleDownloadCV : null)}>
                    <i className="fa-solid fa-file-pdf"></i>
                    <span>{t('talentProfile.viewFullCV', 'View Full CV')}</span>
                  </button>
                )}
                <button className="tp-collapse-btn" onClick={() => setIsCareerExpanded(!isCareerExpanded)}>
                  <i className={`fa-solid ${isCareerExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                </button>
              </div>
            </div>

            {isCareerExpanded && (
              <div>
                {/* Work Experience */}
                <div className="tp-career-section-group">
                  <h4 className="tp-ai-sub-header">{t('talentProfile.workExperience', 'Work Experience')}</h4>
                  {(() => {
                    const jobs = getWorkExperience();
                    const displayJobs = showAllWork ? jobs : jobs.slice(0, 1);

                    if (jobs.length === 0) return <p className="tp-description">{t('talentProfile.noWorkExperience', 'No work experience listed.')}</p>;

                    return (
                      <>
                        {displayJobs.map((job, idx) => {
                          const key = `work-${idx}`;
                          const isExpanded = expandedDescriptions[key] || showAllWork; // Auto expand if load all
                          const desc = job.description || '';
                          const isTruncated = desc.length > 150;
                          const displayDesc = isTruncated && !isExpanded ? `${desc.substring(0, 150)}...` : desc;

                          return (
                            <div className="tp-timeline-item" key={idx}>
                              <div className="tp-timeline-left">
                                <div className={`tp-timeline-dot ${idx === 0 ? '' : 'past'}`}></div>
                                <div className="tp-timeline-line"></div>
                              </div>
                              <div className="tp-timeline-content">
                                <h5 className="tp-role-title">{job.title} @ {job.company}</h5>
                                <p className="tp-date-range">{formatDateRange(job.date_range)}</p>
                                <p className="tp-description">
                                  {displayDesc}
                                  {isTruncated && !showAllWork && (
                                    <span
                                      style={{ color: '#568ded', cursor: 'pointer', marginInlineStart: 5, fontWeight: 600 }}
                                      onClick={() => toggleDescription(key)}
                                    >
                                      {isExpanded ? ` ${t('talentProfile.seeLess', 'See less')}` : ` ${t('talentProfile.seeMore', 'See more')}`}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        {jobs.length > 1 && (
                          <div style={{ marginTop: 10, marginInlineStart: 40 }}>
                            <button
                              onClick={() => setShowAllWork(!showAllWork)}
                              style={{ background: 'transparent', border: 'none', color: '#568ded', fontSize: 14, cursor: 'pointer', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontWeight: 600 }}
                              data-light-control={"quiet"}>
                              {showAllWork ? t('talentProfile.showLess', 'Show Less') : t('talentProfile.loadAll', 'Load All')}
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Education */}
                <div className="tp-career-section-group">
                  <h4 className="tp-ai-sub-header">{t('talentProfile.education', 'Education')}</h4>
                  {(() => {
                    const eduList = getEducation();
                    const displayEdu = showAllEdu ? eduList : eduList.slice(0, 1);

                    if (eduList.length === 0) return <p className="tp-description">{t('talentProfile.noEducation', 'No education listed.')}</p>;

                    return (
                      <>
                        {displayEdu.map((edu, idx) => {
                          const key = `edu-${idx}`;
                          const isExpanded = expandedDescriptions[key] || showAllEdu;
                          const desc = edu.description || '';
                          const isTruncated = desc.length > 150;
                          const displayDesc = isTruncated && !isExpanded ? `${desc.substring(0, 150)}...` : desc;

                          return (
                            <div className="tp-timeline-item" key={idx}>
                              <div className="tp-timeline-left">
                                <div className={`tp-timeline-dot ${idx === 0 ? '' : 'past'}`}></div>
                                <div className="tp-timeline-line"></div>
                              </div>
                              <div className="tp-timeline-content">
                                <h5 className="tp-role-title">{edu.degree}{uiText(" in ")}{edu.field_of_study} @ {edu.school}</h5>
                                <p className="tp-date-range">{formatDateRange(edu.date_range)}</p>
                                <p className="tp-description">
                                  {displayDesc}
                                  {isTruncated && !showAllEdu && (
                                    <span
                                      style={{ color: '#568ded', cursor: 'pointer', marginInlineStart: 5, fontWeight: 600 }}
                                      onClick={() => toggleDescription(key)}
                                    >
                                      {isExpanded ? ` ${t('talentProfile.seeLess', 'See less')}` : ` ${t('talentProfile.seeMore', 'See more')}`}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        {eduList.length > 1 && (
                          <div style={{ marginTop: 10, marginInlineStart: 40 }}>
                            <button
                              onClick={() => setShowAllEdu(!showAllEdu)}
                              style={{ background: 'transparent', border: 'none', color: '#568ded', fontSize: 14, cursor: 'pointer', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontWeight: 600 }}
                              data-light-control={"quiet"}>
                              {showAllEdu ? t('talentProfile.showLess', 'Show Less') : t('talentProfile.loadAll', 'Load All')}
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <TalentQualifications
                  key={profile.metadata.pinecone_id || profile.metadata.user_uid}
                  certificates={getCertifications()}
                  status={profile.metadata.full_cv?.certifications_status}
                  loading={isUnlockingProfile}
                />

                {profile.metadata.full_cv?.languages?.length > 0 && (
                  <div className="tp-career-section-group">
                    <h4 className="tp-ai-sub-header">{t('talentProfile.languages', 'Languages')}</h4>
                    <p className="tp-description">{profile.metadata.full_cv.languages.join(', ')}</p>
                  </div>
                )}

                {profile.metadata.full_cv?.honors?.length > 0 && (
                  <div className="tp-career-section-group">
                    <h4 className="tp-ai-sub-header">{t('talentProfile.honors', 'Honors & awards')}</h4>
                    {profile.metadata.full_cv.honors.map((award, index) => (
                      <div key={index}>
                        <h5 className="tp-role-title">{award.title}</h5>
                        <p className="tp-date-range">{[award.issuer, award.date].filter(Boolean).join(' • ')}</p>
                        {award.description && <p className="tp-description">{award.description}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Skills */}
                <div className="tp-career-section-group">
                  <h4 className="tp-ai-sub-header">{t('talentProfile.skills', 'Skills')}</h4>
                  <div className="tp-skill-tags">
                    {/* Years of Experience for External Profiles */}
                    {!profile.metadata.user_uid && (() => {
                      const years = calculateYearsOfExperience();
                      return years ? (
                        <span
                          className="tp-skill-tag"
                          style={{
                            background: 'rgba(34,197,94,.80)',
                            border: '1px solid rgba(34,197,94,.30)',
                          }}
                        >
                          {t('talentProfile.yearsExperience', '{{years}} years of experience', { years })}
                        </span>
                      ) : null;
                    })()}

                    {(profile.metadata.full_cv?.skills || profile.metadata.tags || []).slice(0, showAllSkills ? undefined : 12).map((skill, idx) => (
                      <span className="tp-skill-tag" key={idx}>{skill}</span>
                    ))}
                    {!showAllSkills && (profile.metadata.full_cv?.skills || profile.metadata.tags || []).length > 12 && (
                      <span className="tp-skill-tag" style={{ backgroundColor: 'rgba(86, 141, 237, 0.5)', cursor: 'pointer' }} onClick={() => setShowAllSkills(true)} >
                        +{(profile.metadata.full_cv?.skills || profile.metadata.tags || []).length - 12}{uiText("more...")}</span>
                    )}
                  </div>
                  {showAllSkills && (
                    <div style={{ marginTop: 10 }}>
                      <button
                        onClick={() => setShowAllSkills(false)}
                        style={{ background: 'transparent', border: 'none', color: '#568ded', fontSize: 14, cursor: 'pointer', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontWeight: 600 }}
                        data-light-control={"quiet"}>
                        {t('talentProfile.showLess', 'Show Less')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Shortlist Popup */}
      {isShortlistPopupOpen && (
        <div className="shortlists-popup">
          <div className="shortlists-popup-content">
            <button
              className="close-popup-button"
              onClick={() => setIsShortlistPopupOpen(false)}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <h1>{t('talentProfile.selectShortlist', 'Select Shortlist')}</h1>
            <div className="shortlists-list">
              {shortlists.map((shortlist, index) => (
                <div
                  key={index}
                  className="shortlist-item"
                  onClick={() => handleAddProfileToShortlist(shortlist.name)}
                >
                  <div className="shortlist-item-content">
                    <p className="shortlist-title">{shortlist.name}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <i
                        className="fa-solid fa-id-badge"
                        style={{ color: '#568ded', opacity: '0.7' }}
                      ></i>
                      <p className="shortlist-profiles">{shortlist.profilesCount} {t('talentProfile.profiles', 'profiles')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Add New Shortlist Button */}
            {!isAddShortlistOpen && (
              <button className="add-shortlist-button" onClick={handleAddShortlistClick}>
                {t('talentProfile.addNewShortlist', 'Add New Shortlist')}
              </button>
            )}
            {/* Add New Shortlist Form */}
            {isAddShortlistOpen && (
              <div className="add-shortlist-form">
                <input
                  type="text"
                  placeholder={t('talentProfile.enterShortlistName', 'Enter shortlist name')}
                  value={newShortlistName}
                  onChange={(e) => setNewShortlistName(e.target.value)}
                />
                <button onClick={handleSaveNewShortlist}>{t('talentProfile.save', 'Save')}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentProfile;
